import {
  hasSubMenu,
  isPathInGroup,
  menuGroups,
} from '~/components/layouts/MainLayout/menuGroups'

/**
 * 「副パネルを × で閉じた」 という user の意思を覚えておく sessionStorage key。
 *
 * design (`design/js/include-nav.js` の `SECONDARY_CLOSED_KEY`) と同じ key・同じ
 * 抑止範囲にする。 localStorage ではなく sessionStorage なのは design の判断に従う:
 * タブを閉じたらリセットしたい (次に開いた時は既定どおり開く)。 localStorage だと
 * 「昔一度閉じた」 が永続して、 いつまでも開かない状態になる。
 */
const SECONDARY_MENU_CLOSED_KEY = 'ebalance-nav-secondary-closed'

/**
 * auto-open を行う最小 viewport 幅 (px)。 これ以下では副パネルが content に
 * 覆いかぶさるため開かない (design `include-nav.js`: `window.innerWidth > 768`)。
 */
const AUTO_OPEN_MIN_VIEWPORT_WIDTH = 768

/** × で閉じた意思を記録する。 記録するのは明示的な close 操作のときだけ */
export const markSecondaryMenuClosedByUser = (): void => {
  try {
    sessionStorage.setItem(SECONDARY_MENU_CLOSED_KEY, '1')
  } catch {
    // sessionStorage が使えない環境 (privacy mode 等) では抑止しない
  }
}

/**
 * 抑止を解除する。 nav の group を自分でクリックした時に呼ぶ
 * (= 副パネルを見る意思表示なので、 過去に閉じた記憶は捨てる)。
 *
 * これが無いと、 一度 × で閉じたあとは遷移先の auto-open が抑止されたままになり、
 * どの group を押しても副パネルが二度と出てこない (design のコメントに実例あり)。
 */
export const clearSecondaryMenuClosedByUser = (): void => {
  try {
    sessionStorage.removeItem(SECONDARY_MENU_CLOSED_KEY)
  } catch {
    // noop
  }
}

export const isSecondaryMenuClosedByUser = (): boolean => {
  try {
    return sessionStorage.getItem(SECONDARY_MENU_CLOSED_KEY) === '1'
  } catch {
    return false
  }
}

type AutoOpenContext = {
  /** × で閉じた意思が記録されているか */
  closedByUser: boolean
  /** 判定時点の viewport 幅 (px) */
  viewportWidth: number
}

/**
 * pathname が属する group の prefix を返す。
 *
 * 2 段構えで引く:
 *
 * 1. **menu item との一致** — URL prefix では表現できない group 横断構造
 *    (例: `/admin/revenue-forecast` が収益管理 group に属す) を正しく引くため、
 *    こちらを先に見る。
 * 2. **group prefix の前方一致** — menu に出ていない下層画面用の fallback。
 *    例: `/demand/forecast-daily` は需要想定 group の menu item ではないが
 *    (一覧から遷移する画面のため)、 副パネルとしては需要想定を出すのが正しい。
 *
 * design (`include-nav.js` の `findActiveGroupIdx`) も「完全一致 → 同ディレクトリ」 の
 * 段階的 fallback で同じことをしている。
 *
 * **単一リンクの group (= システム管理、 #9228) は対象から外す。** 副パネルを持たないため、
 * ここで引いてしまうと「開くべき副パネルがある」 と誤判定し
 * ([[hasMenuGroupForPathname]] が true になる)、 配下の画面へ遷移しても前の group の
 * 副パネルが閉じずに残る (デザイナー指摘 2026-08-12 と同じ症状)。
 */
const findGroupPrefixForPathname = (pathname: string): string | undefined => {
  const groupsWithSubMenu = menuGroups.filter(hasSubMenu)
  return (
    groupsWithSubMenu.find((group) => isPathInGroup(group, pathname)) ??
    groupsWithSubMenu.find(
      (group) =>
        pathname === group.prefix || pathname.startsWith(`${group.prefix}/`),
    )
  )?.prefix
}

/**
 * pathname に対応する menu group が存在するか。
 *
 * false になるのは menu の group に属さない画面 (ホーム / 提出管理 / 個人設定)。
 * これらの画面には「出すべき副パネル」 が無いので、 呼び出し側は開いている副パネルを
 * **閉じる** 必要がある (= [[resolveAutoOpenPrefix]] の undefined は「開かない」 しか
 * 意味しないため、 それだけでは前の group の副パネルが残る)。
 *
 * design (`include-nav.js`) が閉じる処理を持たないのは multi-page で毎回ページごと
 * 作り直されるためで、 SPA では同じロジックにならない (デザイナー指摘 2026-08-12
 * 「個人設定を開いているのに sub side menu がシステム管理が開いたまま」)。
 */
export const hasMenuGroupForPathname = (pathname: string): boolean =>
  findGroupPrefixForPathname(pathname) !== undefined

/**
 * 現在の pathname から auto-open すべき menu group の prefix を導出する。
 * 開かない場合は undefined。
 *
 * 「開かない」 は「閉じる」 ではない。 × で閉じた後や狭い画面では、 既に開いている
 * 副パネルを閉じたりはしない。 該当 group が無い画面で閉じるかどうかは呼び出し側が
 * [[hasMenuGroupForPathname]] で判断する。
 */
export const resolveAutoOpenPrefix = (
  pathname: string,
  { closedByUser, viewportWidth }: AutoOpenContext,
): string | undefined => {
  if (closedByUser) return undefined
  if (viewportWidth <= AUTO_OPEN_MIN_VIEWPORT_WIDTH) return undefined
  return findGroupPrefixForPathname(pathname)
}
